import FindSearch from "./FindSearch";
import CompanyCard from "./CompanyCard";
import CompanyHiring from "./CompanyHiring";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCompaniesContent,
  fetchCompaniesData,
} from "../../../states/actions/fetchCompanies";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import LoadingScreen from "../../common/LoadingScreen";
import FeaturedCompanies from "./FeaturedCompanies";
import LoaderSpinner from "../../common/LoaderSpinner";
import ReactPaginate from "react-paginate";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import paths from "../../../constants/paths";

const ExploreCompanies = () => {
  const [view, setView] = useState("grid");
  const dispatch = useDispatch();
  const data = useSelector((state) => state.fetchCompanies.content);
  const isLoading = useSelector((state) => state.fetchCompanies.isLoading);
  const isSearching = useSelector((state) => state.fetchCompanies.isSearching);
  const featured_companies = useSelector(
    (state) => state.fetchCompanies.featured_companies,
  );
  const companies = useSelector((state) => state.fetchCompanies.companies);
  const occupationName = useSelector(
    (state) => state.fetchCompanies.occupationName,
  );
  const narrowed = useSelector((state) => state.fetchCompanies.narrowed);
  // The occupation view's own "Most active sponsors" strip, returned by the
  // search rather than the page content.
  const sponsorFeatured = useSelector(
    (state) => state.fetchCompanies.sponsorFeatured,
  );
  const totalCompanies = useSelector(
    (state) => state.fetchCompanies.totalCompanies,
  );

  const isSiteSettingsLoading = useSelector(
    (state) => state.fetchSiteSettings.isLoading,
  );
  const plan = useSelector((state) => state.fetchSiteSettings.plan);

  const navigate = useNavigate();
  const { page } = useParams();
  useEffect(() => {
    if (page) {
      setPageNo(Number(page));
    }
  }, [page]);

  const [pageNo, setPageNo] = useState(page);
  const [pageCount, setPageCount] = useState(0);
  // One list, one page size, whichever way the member came in.
  const itemsPerPage = 24;
  // const initialPage = pageNo - 1;
  const initialPage = Math.max(Number(pageNo) - 1, 0);

  const [searchParams] = useSearchParams();
  // const filters = Object.fromEntries([...searchParams.entries()]);
  const filters = {};
  for (const key of searchParams.keys()) {
    const values = searchParams.getAll(key);
    filters[key] = values.length > 1 ? values : values[0];
  }
  const socFilter = filters.soc ? String(filters.soc) : "";
  const hasAnyFilter = Object.keys(filters).length > 0;

  const {
    content,
    meta_desc,
    site_settings,
    locations,
    industry_tags,
    sponsored_roles,
  } = data;
  useEffect(() => {
    dispatch(fetchCompaniesContent());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchCompaniesData({
        page: pageNo,
        ...filters,
      }),
    );
  }, [dispatch, pageNo, searchParams]);

  useEffect(() => {
    // const endOffset = itemOffset + itemsPerPage;
    // setCurrentItems(jobs.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(totalCompanies / itemsPerPage));
  }, [totalCompanies]);

  // Drops the profiled-only filters and keeps the occupation, so one click
  // goes from "the 14 we have profiled" back to everyone who sponsored it.
  const handleShowAllSponsors = () => {
    const keep = new URLSearchParams();
    if (socFilter) keep.append("soc", socFilter);
    const kw = searchParams.get("searchKeyword");
    if (kw) keep.append("searchKeyword", kw);
    navigate(`/explore-companies/1?${keep.toString()}`);
  };

  const handlePageClick = (event) => {
    // console.log(event.selected);
    window.scrollTo({ top: 300, behavior: "smooth" });
    let pageNumber = event.selected + 1;
    const isAlreadyInBasePath =
      typeof window !== "undefined" &&
      window.location.pathname.includes(`${paths.SITE_BASE_URL}`);
    const basePath = isAlreadyInBasePath ? "" : `${paths.SITE_BASE_URL}`;
    const isAllowedPlan = plan && [5, 6, 7, 8].includes(Number(plan.id));

    if (pageNumber > 1 && !isAllowedPlan) {
      navigate(basePath + "/subscription");
      return;
    }
    setPageNo(pageNumber);
    const queryString = searchParams.toString();

    // navigate(`${basePath}/explore-companies/${pageNumber}`);
    navigate(
      `${basePath}/explore-companies/${pageNumber}${
        queryString ? `?${queryString}` : ""
      }`,
    );
    // if (pageNumber > 2) {
    //   if (!plan) {
    //     navigate(basePath + "/subscription");
    //     return false;
    //   } else {
    //     setPageNo(event.selected + 1);
    //     navigate(`${basePath}/explore-companies/${event.selected + 1}`);
    //   }
    // } else {
    //   setPageNo(event.selected + 1);
    //   navigate(`${basePath}/explore-companies/${event.selected + 1}`);
    // }
  };

  useEffect(() => {
    if (!page) return;
    if (isSiteSettingsLoading) return;
    const pageNumber = Number(page);
    if (!plan) {
      if (pageNumber > 1) {
        navigate("/subscription", { replace: true });
      }
      return;
    }

    const isAllowedPlan = [5, 6, 7, 8].includes(Number(plan.id));

    if (pageNumber > 1 && !isAllowedPlan) {
      navigate("/subscription", { replace: true });
    }
  }, [page, plan, isSiteSettingsLoading, navigate]);

  useDocumentTitle(data?.page_title);

  return (
    <>
      {isLoading || isSiteSettingsLoading ? (
        <LoadingScreen />
      ) : (
        <>
          <section id="find_jobs">
            <div className="contain">
              <div className="top_text">
                <h2>{content?.banner_heading}</h2>
                <p>{content?.banner_tagline}</p>
                <p className="saved_link">
                  <Link to="/saved-companies">Your saved companies</Link>
                </p>
              </div>
              <FindSearch
                locations={locations}
                industry_tags={industry_tags}
                sponsored_roles={sponsored_roles}
                plan={plan}
              />
              {/* Hidden as soon as anything is filtered. A strip of promoted
                  companies above "5 results for amazon" reads as part of the
                  result. */}
              {!hasAnyFilter && featured_companies && featured_companies.length > 0 && (
                <FeaturedCompanies
                  content={content}
                  featured_companies={featured_companies}
                />
              )}

              {socFilter && sponsorFeatured && sponsorFeatured.length > 0 && (
                <div className="feature_companies_wrapper">
                  <div className="top_head">
                    <div className="text">
                      <h3>Most active sponsors</h3>
                      <p>
                        The employers who sponsored this role most often in
                        2025.
                      </p>
                    </div>
                  </div>
                  <div className="row form_row">
                    {sponsorFeatured.map((c, i) => (
                      <div
                        className="col-md-3 d-flex"
                        key={`sf-${c.name}-${i}`}
                      >
                        <CompanyCard company={c} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="all_companies_wrapper" id="sec_comp_pg">
                <div className="top_head">
                  <div className="text">
                    <h3>
                      {/* Never lowercased: the Home Office writes these in
                          sentence case already, and forcing it turned "IT
                          business analysts" into "it business analysts". */}
                      {socFilter && occupationName
                        ? `Who sponsored ${occupationName}`
                        : content?.companies_heading}
                    </h3>
                    <p>
                      {socFilter
                        ? `${Number(totalCompanies).toLocaleString()} ${
                            totalCompanies === 1 ? "employer" : "employers"
                          }${
                            filters.searchKeyword
                              ? ` matching "${filters.searchKeyword}"`
                              : ""
                          } assigned a Certificate of Sponsorship for this role in 2025.`
                        : `${Number(totalCompanies).toLocaleString()} ${content?.companies_tagline ?? ""}`}
                    </p>
                  </div>
                  <div className="btn_blk">
                    <button
                      type="button"
                      className={view === "grid" ? "active" : ""}
                      onClick={() => setView("grid")}
                    >
                      <img src="/images/icon_grid.png" alt="" />
                      Grid
                    </button>

                    <button
                      type="button"
                      className={view === "list" ? "active" : ""}
                      onClick={() => setView("list")}
                    >
                      <img src="/images/icon_list.png" alt="" />
                      List
                    </button>
                  </div>
                </div>

                {narrowed && (
                  <div className="directory_note">
                    <span>
                      A rating, industry or location filter only exists for
                      companies we have profiled, so this is a slice of the
                      employers who sponsored this role.
                    </span>
                    <button type="button" onClick={handleShowAllSponsors}>
                      Show all sponsors
                    </button>
                  </div>
                )}

                {isSearching ? (
                  <div className="text-center">
                    <LoaderSpinner />
                  </div>
                ) : companies && companies.length > 0 ? (
                  <>
                    <div className="row form_row">
                      {companies.map((company, i) => (
                        <div
                          key={`${company.id ?? "cos"}-${company.name}-${i}`}
                          className={`${view === "grid" ? "col-md-6" : "col-md-12"} d-flex`}
                        >
                          <CompanyCard company={company} />
                        </div>
                      ))}

                      <ReactPaginate
                        forcePage={initialPage}
                        pageCount={pageCount}
                        onPageChange={handlePageClick}
                        breakLabel="..."
                        previousLabel=""
                        nextLabel=""
                        pageRangeDisplayed={4}
                        marginPagesDisplayed={1}
                        containerClassName="pagination"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        activeClassName="active"
                        previousClassName="prev_btn"
                        previousLinkClassName="page-link"
                        nextClassName="next_btn"
                        nextLinkClassName="page-link"
                        breakClassName=""
                        breakLinkClassName="break-link"
                        renderOnZeroPageCount={false}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center my-5">
                    <h4>No companies found.</h4>
                  </div>
                )}
              </div>

              <CompanyHiring
                heading={content?.cta_heading}
                tagline={content?.cta_tagline}
                btn1_txt={content?.cta_btn_1_txt}
                btn2_txt={content?.cta_btn_2_txt}
              />
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default ExploreCompanies;
